import {
  createTemplate,
  deleteTemplate,
  getTemplate,
  searchTemplates,
  updateTemplate,
  type ContractGenerationTemplateCreateRequest,
  type ContractGenerationTemplateInDb,
  type ContractGenerationTemplateSearchResult,
  type ContractGenerationTemplateUpdate,
  type FilterableSearchQuery,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

export interface TemplateSearchQuery {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

export const TemplatesService = {
  async search(query: TemplateSearchQuery = {}): Promise<ContractGenerationTemplateSearchResult> {
    try {
      const pageSize = query.pageSize ?? 50;
      const page = query.page ?? 1;
      const body: FilterableSearchQuery = {
        text: query.text ?? '',
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_key: query.sortKey ?? 'created_at',
        sort_direction: query.sortDirection ?? 'desc',
      };

      const response = await searchTemplates({ body });
      if (!response.data) {
        throw new Error('Failed to search templates');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to search templates.'));
    }
  },

  async getById(uid: string): Promise<ContractGenerationTemplateInDb> {
    try {
      const response = await getTemplate({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to fetch template');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to fetch template.'));
    }
  },

  /**
   * Create a template from a .docx, sent with the request — the backend files
   * it in the system vault, never in anyone's vault or as a contract.
   * `variantName` names the template's first variant — every template has at
   * least one.
   */
  async create(
    file: File,
    name: string,
    variantName: string,
    description?: string | null
  ): Promise<ContractGenerationTemplateInDb> {
    try {
      const body: ContractGenerationTemplateCreateRequest = {
        file,
        name,
        variant_name: variantName,
        description: description ?? null,
      };

      const response = await createTemplate({ body });
      if (!response.data) {
        throw new Error('Failed to create template');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create template.'));
    }
  },

  async update(
    uid: string,
    payload: ContractGenerationTemplateUpdate
  ): Promise<ContractGenerationTemplateInDb> {
    try {
      const response = await updateTemplate({ path: { uid }, body: payload });
      if (!response.data) {
        throw new Error('Failed to update template');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to update template.'));
    }
  },

  /** Delete a template — and the drafts made from it; documents saved from it stay. */
  async delete(uid: string): Promise<ContractGenerationTemplateInDb> {
    try {
      const response = await deleteTemplate({ path: { uid } });
      if (!response.data) {
        throw new Error('Failed to delete template');
      }
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete template.'));
    }
  },
};
